import React from 'react';
import { ArrowRight, CheckCircle2, TrendingUp, Shield, Users, Star, Clock } from 'lucide-react';
import { Button } from '../ui/button';
import { heroData, contactData } from '../../data/mock';

const HeroSection = () => {
  const features = [
    'Análise de SCR e Rating',
    'Atuação via CDC',
    'Atendimento Nacional'
  ];

  const stats = [
    { icon: Users, value: '+2.500', label: 'Clientes Atendidos' },
    { icon: Star, value: '4.9', label: 'Avaliação Google' },
    { icon: Clock, value: '7 anos', label: 'de Experiência' },
  ];

  return (
    <section className="relative min-h-screen flex items-center pt-20 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-slate-100" />
      
      {/* Subtle pattern overlay */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-20 right-20 w-96 h-96 bg-emerald-100 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-10 w-80 h-80 bg-amber-50 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Content */}
          <div className="space-y-8">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-slate-800 text-white rounded-full text-sm font-medium">
              <Shield className="w-4 h-4 text-amber-400" />
              Especialistas em Regularização de Crédito
            </div>

            {/* Headline */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight">
              {heroData.headline}
            </h1>

            {/* Subheadline */}
            <p className="text-lg md:text-xl text-slate-600 leading-relaxed max-w-xl">
              {heroData.subheadline}
            </p>

            {/* Features List */}
            <div className="flex flex-wrap gap-4">
              {features.map((feature, index) => (
                <div key={index} className="flex items-center gap-2 text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                  <span className="font-medium">{feature}</span>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                size="lg"
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-lg px-8 py-6 rounded-xl shadow-lg shadow-emerald-200 hover:shadow-xl hover:shadow-emerald-200 transition-all duration-300"
                asChild
              >
                <a href={contactData.whatsappLink} target="_blank" rel="noopener noreferrer">
                  {heroData.ctaText}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </a>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-slate-300 text-slate-700 font-semibold text-lg px-8 py-6 rounded-xl hover:bg-slate-50 transition-all duration-300"
                asChild
              >
                <a href="#problema">Entenda o Problema</a>
              </Button>
            </div>

            {/* Social Proof Stats */}
            <div className="flex flex-wrap gap-6 pt-4 border-t border-slate-200">
              {stats.map((stat, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                    <stat.icon className="w-5 h-5 text-slate-600" />
                  </div>
                  <div>
                    <p className="text-lg font-bold text-slate-800">{stat.value}</p>
                    <p className="text-xs text-slate-500">{stat.label}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Visual Element */}
          <div className="relative hidden lg:block">
            <div className="relative bg-white rounded-3xl shadow-2xl shadow-slate-200 p-8 border border-slate-100">
              {/* Stats Card */}
              <div className="space-y-6">
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl">
                  <div>
                    <p className="text-sm text-slate-500 font-medium">Análise de SCR</p>
                    <p className="text-2xl font-bold text-slate-800">Banco Central</p>
                  </div>
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                    <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl">
                  <div>
                    <p className="text-sm text-slate-500 font-medium">Rating Bancário</p>
                    <p className="text-2xl font-bold text-slate-800">Concentre</p>
                  </div>
                  <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-amber-600" />
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl">
                  <div>
                    <p className="text-sm text-slate-500 font-medium">Bureaus de Crédito</p>
                    <p className="text-2xl font-bold text-slate-800">Serasa • SPC • Boa Vista</p>
                  </div>
                  <div className="w-12 h-12 bg-slate-200 rounded-xl flex items-center justify-center">
                    <Shield className="w-6 h-6 text-slate-600" />
                  </div>
                </div>

                <div className="p-4 bg-emerald-600 rounded-2xl text-center">
                  <p className="text-emerald-100 text-sm font-medium">Resultado da Perícia</p>
                  <p className="text-white text-xl font-bold">Dados para Regularização</p>
                </div>
              </div>
            </div>

            {/* Floating elements */}
            <div className="absolute -top-4 -right-4 w-20 h-20 bg-amber-400 rounded-2xl shadow-lg flex items-center justify-center rotate-12">
              <Shield className="w-10 h-10 text-white -rotate-12" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
